import React, { useState, useEffect, useCallback } from 'react';

import uploadService from '../services/uploadService';
import { useToast } from '../contexts/ToastContext';
import './Feeds.css';

const AdminFailedUploads = () => {
    const toast = useToast();
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
    const [retrying, setRetrying] = useState(null);
    const [pendingRetry, setPendingRetry] = useState(null);

    const fetchFailedVideos = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                status: 'failed'
            };

            const result = await uploadService.getAllUploads(params);

            if (result.success) {
                setVideos(result.uploads);
                setPagination(prev => ({ ...prev, ...result.pagination }));
            }
        } catch (error) {
            console.error('Error fetching failed videos:', error);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit]);

    useEffect(() => {
        fetchFailedVideos();
    }, [fetchFailedVideos]);

    const handleRetry = async (session) => {
        setPendingRetry(null);
        try {
            setRetrying(session.uploadId);
            const result = await uploadService.retryUpload(session.uploadId);

            if (result.success) {
                setVideos(prev => prev.filter(v => v.uploadId !== session.uploadId));
                toast.success('Upload reset — the operator can now resume it.');
            } else {
                toast.error('Failed to retry upload: ' + result.message);
            }
        } catch (error) {
            console.error('Error retrying upload:', error);
            toast.error('Error retrying upload. Please try again.');
        } finally {
            setRetrying(null);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <div className="feeds-page">
            <div className="table-section">
                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading failed uploads...</p>
                    </div>
                ) : videos.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-check-circle" style={{ fontSize: '48px', color: '#10b981', opacity: 0.2, marginBottom: '16px' }}></i>
                        <p>No failed uploads</p>
                        <p className="empty-hint">All uploads have been processed successfully</p>
                    </div>
                ) : (
                    <>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>AWB Number</th>
                                    <th>User</th>
                                    <th>Scan Type</th>
                                    <th>Error Details</th>
                                    <th>Failed At</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {videos.map(session => (
                                    <tr key={session._id}>
                                        <td className="awb-cell">{session.awb}</td>
                                        <td className="user-cell">
                                            <span className="name">{session.userName || 'Unknown'}</span>
                                        </td>
                                        <td>
                                            <span className={`scan-badge ${session.scanType}`}>
                                                {session.scanType}
                                            </span>
                                        </td>
                                        <td className="error-cell">
                                            <div title={session.errorDetails}>
                                                {session.errorDetails || 'Unknown error'}
                                            </div>
                                        </td>
                                        <td className="date-cell">{formatDate(session.createdAt || session.startTime)}</td>
                                        <td>
                                            {pendingRetry === session.uploadId ? (
                                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>Retry?</span>
                                                    <button
                                                        className="btn btn-sm btn-warning"
                                                        onClick={() => handleRetry(session)}
                                                        disabled={retrying === session.uploadId}
                                                    >
                                                        Yes
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => setPendingRetry(null)}
                                                    >
                                                        No
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    className="btn btn-sm btn-warning"
                                                    onClick={() => setPendingRetry(session.uploadId)}
                                                    disabled={retrying === session.uploadId}
                                                >
                                                    {retrying === session.uploadId ? (
                                                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>
                                                    ) : (
                                                        <i className="fas fa-redo" style={{ marginRight: '6px' }}></i>
                                                    )}
                                                    Retry
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="pagination">
                            <span className="pagination-info">
                                Showing {videos.length} of {pagination.total} failed uploads
                            </span>
                            <div className="pagination-controls">
                                <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={pagination.page <= 1}
                                >
                                    Previous
                                </button>
                                <span className="page-info">
                                    Page {pagination.page} of {pagination.pages}
                                </span>
                                <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={pagination.page >= pagination.pages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminFailedUploads;
